import { state, clipDurationSeconds, hasAnimation } from '../state.ts';
import { findCamera } from '../camera/types.ts';

export interface CameraStripHandlers {
  onAddCut: () => void;
  /** Drag a cut boundary to a new time (seconds; snapping/clamping in main). */
  onMoveCut: (index: number, seconds: number) => void;
  onDeleteCut: (index: number) => void;
  onReassignShot: (index: number, cameraId: string) => void;
  /** Click a shot block: select it (and its camera). */
  onSelectShot: (index: number) => void;
  /** Double-click a shot block: look through its camera. */
  onLookThroughShot: (index: number) => void;
  /** Click a keyframe diamond: select the key (and scrub to it). */
  onSelectKeyframe: (index: number) => void;
  /** Drag a keyframe diamond to retime it (seconds; snapping in main). */
  onMoveKeyframe: (index: number, seconds: number) => void;
}

/**
 * Controlbar camera strip: one tinted block per program shot, draggable cut
 * boundaries, a playhead line, and an inline camera <select> on the selected
 * shot. Percent-positioned against clip duration, mirroring #mark-range.
 */
export function createCameraStrip(handlers: CameraStripHandlers) {
  const strip = byId<HTMLDivElement>('camera-strip');
  const playhead = byId<HTMLDivElement>('strip-playhead');
  const cutBtn = byId<HTMLButtonElement>('cut-btn');

  let selectedShot: number | null = null;
  let dragging = false;
  let lastSignature = '';
  /** Live references for in-place repositioning while dragging. */
  let blockEls: HTMLDivElement[] = [];
  let handleEls: { el: HTMLDivElement; index: number }[] = [];
  let diamondEls: { el: HTMLDivElement; index: number }[] = [];

  cutBtn.addEventListener('click', () => handlers.onAddCut());
  strip.addEventListener('click', (e) => {
    if (e.target === strip) {
      selectedShot = null;
      lastSignature = '';
      update();
    }
  });

  /** Segment bounds in seconds for shot i (end = next cut or clip end). */
  function shotBounds(i: number): { start: number; end: number } {
    const shots = state.cameras.shots;
    const dur = Math.max(clipDurationSeconds(), 1e-6);
    return {
      start: shots[i]?.time ?? 0,
      end: i + 1 < shots.length ? shots[i + 1].time : dur,
    };
  }

  function rebuild(): void {
    for (const el of blockEls) el.remove();
    for (const h of handleEls) h.el.remove();
    for (const d of diamondEls) d.el.remove();
    blockEls = [];
    handleEls = [];
    diamondEls = [];

    const cams = state.cameras.cameras;
    if (cams.length === 0) return;
    rebuildDiamonds();
    const shots = state.cameras.shots;
    const dur = Math.max(clipDurationSeconds(), 1e-6);

    if (shots.length === 0) {
      // No cuts yet: the program falls back to the first camera for the whole clip.
      const block = makeBlock(cams[0].color, cams[0].name, true);
      block.style.left = '0%';
      block.style.width = '100%';
      block.title = 'Program shows this camera for the whole clip. Press C to cut.';
      strip.appendChild(block);
      blockEls.push(block);
      return;
    }

    shots.forEach((shot, i) => {
      const cam = findCamera(state.cameras, shot.cameraId);
      const { start, end } = shotBounds(i);
      const block = makeBlock(cam?.color ?? 0x888888, cam?.name ?? '?', false);
      block.style.left = `${(start / dur) * 100}%`;
      block.style.width = `${Math.max(0, ((end - start) / dur) * 100)}%`;
      block.classList.toggle('selected', i === selectedShot);
      block.addEventListener('click', (e) => {
        e.stopPropagation();
        selectedShot = i;
        lastSignature = '';
        handlers.onSelectShot(i);
      });
      block.addEventListener('dblclick', (e) => {
        e.stopPropagation();
        handlers.onLookThroughShot(i);
      });

      // The selected shot exposes an inline select to reassign its camera.
      if (i === selectedShot && cams.length > 1) {
        const select = document.createElement('select');
        select.className = 'strip-select';
        select.title = 'Camera for this shot';
        for (const c of cams) {
          const opt = document.createElement('option');
          opt.value = c.id;
          opt.textContent = c.name;
          opt.selected = c.id === shot.cameraId;
          select.appendChild(opt);
        }
        select.addEventListener('click', (e) => e.stopPropagation());
        select.addEventListener('change', () => handlers.onReassignShot(i, select.value));
        block.appendChild(select);
      }

      strip.appendChild(block);
      blockEls.push(block);

      // Boundary handle at the start of every shot except the first.
      if (i > 0) {
        const handle = document.createElement('div');
        handle.className = 'strip-handle';
        handle.title = 'Drag to move the cut · click + Delete to remove';
        handle.style.left = `${(start / dur) * 100}%`;
        attachHandleDrag(handle, i);
        strip.appendChild(handle);
        handleEls.push({ el: handle, index: i });
      }
    });
  }

  function makeBlock(color: number, name: string, fallback: boolean): HTMLDivElement {
    const block = document.createElement('div');
    block.className = 'strip-shot';
    block.classList.toggle('fallback', fallback);
    const rgb = `${(color >> 16) & 255}, ${(color >> 8) & 255}, ${color & 255}`;
    block.style.background = `rgba(${rgb}, 0.32)`;
    block.style.borderLeft = `3px solid rgb(${rgb})`;
    const label = document.createElement('span');
    label.textContent = name;
    block.appendChild(label);
    return block;
  }

  /** Keyframe diamonds for the selected camera, drawn above the shot blocks. */
  function rebuildDiamonds(): void {
    const def = findCamera(state.cameras, state.selectedCameraId);
    if (!def || def.keyframes.length === 0) return;
    const dur = Math.max(clipDurationSeconds(), 1e-6);
    def.keyframes.forEach((k, index) => {
      const el = document.createElement('div');
      el.className = 'strip-key';
      el.classList.toggle('selected', index === state.selectedKeyframe);
      el.style.left = `${(k.time / dur) * 100}%`;
      el.title = `Keyframe at ${k.time.toFixed(2)}s — drag to retime`;
      el.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragging = true;
        try {
          el.setPointerCapture(e.pointerId);
        } catch {
          /* synthetic events may lack a real pointer */
        }
        const rect = strip.getBoundingClientRect();
        let movedPx = 0;
        const startX = e.clientX;
        const move = (ev: PointerEvent): void => {
          movedPx = Math.max(movedPx, Math.abs(ev.clientX - startX));
          if (movedPx < 3) return;
          const frac = Math.min(Math.max((ev.clientX - rect.left) / rect.width, 0), 1);
          handlers.onMoveKeyframe(index, frac * clipDurationSeconds());
        };
        const up = (): void => {
          dragging = false;
          el.removeEventListener('pointermove', move);
          el.removeEventListener('pointerup', up);
          if (movedPx < 3) handlers.onSelectKeyframe(index); // it was a click
          lastSignature = '';
          update();
        };
        el.addEventListener('pointermove', move);
        el.addEventListener('pointerup', up);
      });
      strip.appendChild(el);
      diamondEls.push({ el, index });
    });
  }

  function attachHandleDrag(handle: HTMLDivElement, index: number): void {
    handle.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragging = true;
      selectedShot = index;
      try {
        handle.setPointerCapture(e.pointerId);
      } catch {
        /* synthetic events may lack a real pointer */
      }
      const rect = strip.getBoundingClientRect();
      const move = (ev: PointerEvent): void => {
        const frac = Math.min(Math.max((ev.clientX - rect.left) / rect.width, 0), 1);
        handlers.onMoveCut(index, frac * clipDurationSeconds());
      };
      const up = (): void => {
        dragging = false;
        handle.removeEventListener('pointermove', move);
        handle.removeEventListener('pointerup', up);
        lastSignature = '';
        update();
      };
      handle.addEventListener('pointermove', move);
      handle.addEventListener('pointerup', up);
    });
  }

  /** While dragging, move existing blocks/handles instead of rebuilding DOM. */
  function reposition(): void {
    const dur = Math.max(clipDurationSeconds(), 1e-6);
    const shots = state.cameras.shots;
    if (shots.length === 0) return;
    blockEls.forEach((el, i) => {
      const { start, end } = shotBounds(i);
      el.style.left = `${(start / dur) * 100}%`;
      el.style.width = `${Math.max(0, ((end - start) / dur) * 100)}%`;
    });
    for (const h of handleEls) {
      h.el.style.left = `${((shots[h.index]?.time ?? 0) / dur) * 100}%`;
    }
  }

  /** Reposition diamonds in place while one is being dragged. */
  function repositionDiamonds(): void {
    const def = findCamera(state.cameras, state.selectedCameraId);
    if (!def) return;
    const dur = Math.max(clipDurationSeconds(), 1e-6);
    for (const d of diamondEls) {
      const k = def.keyframes[d.index];
      if (k) d.el.style.left = `${(k.time / dur) * 100}%`;
    }
  }

  /** Delete the selected cut (Delete key). Returns true when something happened. */
  function deleteSelected(): boolean {
    if (selectedShot === null || state.cameras.shots.length === 0) return false;
    const index = selectedShot;
    selectedShot = null;
    lastSignature = '';
    handlers.onDeleteCut(index);
    return true;
  }

  /** Reflect current state (called on every notify()). */
  function update(): void {
    const cams = state.cameras.cameras;
    strip.hidden = !hasAnimation() || cams.length === 0;
    cutBtn.disabled = !hasAnimation() || cams.length === 0 || state.exporting;
    if (strip.hidden) return;

    // Playhead line always tracks the current frame.
    const pct = state.totalFrames > 0 ? (state.currentFrame / state.totalFrames) * 100 : 0;
    playhead.style.left = `${pct}%`;

    if (dragging) {
      reposition();
      repositionDiamonds();
      return;
    }
    if (selectedShot !== null && selectedShot >= state.cameras.shots.length) selectedShot = null;
    const selectedCam = findCamera(state.cameras, state.selectedCameraId);
    const signature = JSON.stringify([
      state.cameras.shots,
      cams.map((c) => [c.id, c.name, c.color]),
      selectedShot,
      state.totalFrames,
      state.selectedCameraId,
      state.selectedKeyframe,
      selectedCam?.keyframes.map((k) => k.time),
    ]);
    if (signature !== lastSignature) {
      lastSignature = signature;
      rebuild();
    }
  }

  return { update, deleteSelected };
}

function byId<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Missing element #${id}`);
  return el as T;
}
